import React, { useState } from 'react';
import Badge from '../../components/Badge';
import DataTable from '../../DataTable';
import Modal from '../../Modal';

/**
 * ダッシュボード - 洗浄履歴概要ページ
 */
function Overview({
  dashboardData,
  theme,
  historyDateFrom,
  setHistoryDateFrom,
  historyDateTo,
  setHistoryDateTo,
  historyStatusFilter,
  setHistoryStatusFilter,
  historySearchText,
  setHistorySearchText,
  historySort,
  setHistorySort,
  historyPage,
  setHistoryPage,
  historyPageSize,
  setHistoryPageSize,
  historyColumns,
  setHistoryColumns,
  defaultHistoryColumns
}) {
  const [showConveyorModal, setShowConveyorModal] = useState(false);
  const [selectedConveyor, setSelectedConveyor] = useState(null);
  const locationLinkStyle = {
    border: 'none',
    background: 'none',
    padding: 0,
    margin: 0,
    font: 'inherit',
    fontWeight: 700,
    color: '#2b6cb0',
    textDecoration: 'underline',
    cursor: 'pointer'
  };

  const openConveyorDetail = (row) => {
    const detail = {
      locationName: row.location_name || '-',
      locationCode: row.location_code || '-',
      rfidTagId: row.rfid_tag_id || '-',
      conveyorSpec: {
        maker: 'アイウォット機工',
        model: 'IW-CV-300',
        motorModel: 'IM-200V-90W',
        ratedCurrent: '1.8A',
        conveyorType: '平ベルトコンベア',
        totalLength: '2000L',
        effectiveWidth: '300W',
        pulleyDiameter: 'φ80',
        beltSpeed: '15m/min',
        inspectionCycle: '3か月'
      },
      beltSpec: {
        maker: 'アイウォットベルト',
        modelNo: 'NS41UFG0/2Ｗ',
        material: 'ウレタン',
        length: '２０００Ｌ',
        width: '３００ｗ',
        thickness: '1.6mm',
        jointType: 'フィンガーエンドレス',
        antiWanderCleat: 'ＭＳ桟　裏面センター',
        inStock: 'あり'
      },
      history: [
        {
          installedAt: '2026-04-01',
          removedAt: '-',
          damageType: '軽微な摩耗',
          damagePhoto: '未登録',
          estimatedCause: '通常摩耗（定期交換推奨）'
        }
      ]
    };

    setSelectedConveyor(detail);
    setShowConveyorModal(true);
  };

  // YYYY/mm/dd HH:MM:SS形式でフォーマット
  const formatDateTime = (dt) => {
    if (!dt) return '-';
    const d = new Date(dt);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
  };
  
  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}分${secs}秒`;
  };
  
  const getStatusBadge = (status) => {
    const statusMap = {
      'started': { text: '洗浄中', class: 'info' },
      'completed': { text: '完了', class: 'good' },
      'unknown_tag': { text: '不明タグ', class: 'warn' },
      'failed': { text: '失敗', class: 'bad' }
    };
    const mapped = statusMap[status] || { text: status, class: 'info' };
    return <Badge text={mapped.text} kind={mapped.class} />;
  };

  // フィルタリングとソート
  let filteredHistories = dashboardData.cleaning_histories;
  
  // ステータスフィルター
  if (historyStatusFilter !== 'all') {
    filteredHistories = filteredHistories.filter(h => h.result_status === historyStatusFilter);
  }
  
  // 期間フィルター
  if (historyDateFrom) {
    const fromDate = new Date(historyDateFrom);
    filteredHistories = filteredHistories.filter(h => {
      if (!h.started_at) return false;
      return new Date(h.started_at) >= fromDate;
    });
  }
  if (historyDateTo) {
    const toDate = new Date(historyDateTo);
    toDate.setHours(23, 59, 59, 999); // 当日の終わりまで含める
    filteredHistories = filteredHistories.filter(h => {
      if (!h.started_at) return false;
      return new Date(h.started_at) <= toDate;
    });
  }
  
  // フリーワード検索
  if (historySearchText) {
    const searchLower = historySearchText.toLowerCase();
    filteredHistories = filteredHistories.filter(h => {
      return (
        String(h.cleaning_history_id).includes(searchLower) ||
        (h.washer_name && h.washer_name.toLowerCase().includes(searchLower)) ||
        (h.washer_code && h.washer_code.toLowerCase().includes(searchLower)) ||
        (h.sensor_name && h.sensor_name.toLowerCase().includes(searchLower)) ||
        (h.mac_address && h.mac_address.toLowerCase().includes(searchLower)) ||
        (h.location_name && h.location_name.toLowerCase().includes(searchLower)) ||
        (h.location_path && h.location_path.toLowerCase().includes(searchLower)) ||
        (h.rfid_tag_id && h.rfid_tag_id.toLowerCase().includes(searchLower))
      );
    });
  }
  
  // ソート
  filteredHistories = [...filteredHistories].sort((a, b) => {
    const { column, order } = historySort;
    let aVal = a[column];
    let bVal = b[column];
    
    // null/undefined処理
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    
    // 数値比較
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return order === 'asc' ? aVal - bVal : bVal - aVal;
    }
    
    // 文字列・日付比較
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });

  // ページング
  const totalCount = filteredHistories.length;
  const startIndex = (historyPage - 1) * historyPageSize;
  const endIndex = startIndex + historyPageSize;
  const pagedHistories = filteredHistories.slice(startIndex, endIndex);

  const handlePageSizeChange = (newSize) => {
    setHistoryPageSize(newSize);
    setHistoryPage(1); // ページサイズ変更時はページを1に戻す
  };

  const handleFilterChange = () => {
    setHistoryPage(1); // フィルター変更時はページを1に戻す
  };

  return (
    <div>
      <h2>洗浄履歴</h2>
      {dashboardData.cleaning_histories.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <h3>まだデータがありません</h3>
          <p>センサーから洗浄データが送信されると表示されます。</p>
        </div>
      ) : (
        <div>
          {/* フィルター・検索エリア */}
          <div style={{ 
            marginBottom: '16px', 
            padding: '16px', 
            backgroundColor: theme === 'dark' ? '#2f3844' : '#f7fafc', 
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {/* 期間指定とステータスフィルター */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 'bold' }}>期間:</span>
              <input
                type="date"
                value={historyDateFrom}
                onChange={(e) => { setHistoryDateFrom(e.target.value); handleFilterChange(); }}
                style={{
                  padding: '6px 12px',
                  border: theme === 'dark' ? '1px solid #3d4858' : '1px solid #cbd5e0',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: theme === 'dark' ? '#252d3a' : 'white',
                  color: theme === 'dark' ? '#dae3ed' : '#2d3748'
                }}
              />
              <span>〜</span>
              <input
                type="date"
                value={historyDateTo}
                onChange={(e) => { setHistoryDateTo(e.target.value); handleFilterChange(); }}
                style={{
                  padding: '6px 12px',
                  border: theme === 'dark' ? '1px solid #3d4858' : '1px solid #cbd5e0',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: theme === 'dark' ? '#252d3a' : 'white',
                  color: theme === 'dark' ? '#dae3ed' : '#2d3748'
                }}
              />
              <span style={{ fontWeight: 'bold', marginLeft: '12px' }}>ステータス:</span>
              {['all', 'started', 'completed'].map(status => (
                <button
                  key={status}
                  onClick={() => { setHistoryStatusFilter(status); handleFilterChange(); }}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: historyStatusFilter === status ? '#3182ce' : (theme === 'dark' ? '#3d4858' : '#e2e8f0'),
                    color: historyStatusFilter === status ? 'white' : (theme === 'dark' ? '#dae3ed' : '#2d3748'),
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {status === 'all' ? '全て' :
                   status === 'started' ? '洗浄中' : '完了'}
                </button>
              ))}
            </div>

            {/* フリーワード検索 */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 'bold' }}>検索:</span>
              <input
                type="text"
                placeholder="ID、洗浄機、センサー、設置先、RFIDタグ..."
                value={historySearchText}
                onChange={(e) => { setHistorySearchText(e.target.value); handleFilterChange(); }}
                style={{
                  padding: '6px 12px',
                  border: theme === 'dark' ? '1px solid #3d4858' : '1px solid #cbd5e0',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '200px',
                  flex: 1,
                  backgroundColor: theme === 'dark' ? '#252d3a' : 'white',
                  color: theme === 'dark' ? '#dae3ed' : '#2d3748'
                }}
              />
            </div>
          </div>

          <DataTable
            columns={historyColumns}
            data={pagedHistories}
            defaultColumns={defaultHistoryColumns}
            onColumnsChange={setHistoryColumns}
            localStorageKey="historyColumns"
            sortConfig={historySort}
            onSortChange={(config) => setHistorySort(config)}
            currentPage={historyPage}
            pageSize={historyPageSize}
            totalItems={totalCount}
            onPageChange={setHistoryPage}
            onPageSizeChange={handlePageSizeChange}
            idField="cleaning_history_id"
            emptyMessage="データがありません"
            renderCell={(row, column, cellStyle) => {
              switch (column.id) {
                case 'cleaning_history_id':
                  return <td key={column.id} style={cellStyle}>{row.cleaning_history_id}</td>;
                case 'washer_name':
                  return (
                    <td key={column.id} style={cellStyle}>
                      {row.washer_name || '-'}
                      {row.washer_code && <div style={{fontSize: '0.85em', color: '#718096'}}>({row.washer_code})</div>}
                    </td>
                  );
                case 'sensor_name':
                  return <td key={column.id} style={cellStyle}>{row.sensor_name || row.mac_address || '-'}</td>;
                case 'location_name':
                  return (
                    <td key={column.id} style={cellStyle}>
                      {(() => {
                        const path = row.location_path || row.location_name || '-';
                        if (path === '-') return '-';
                        const parts = path.split(' > ');
                        if (parts.length === 1) {
                          return (
                            <div>
                              {row.rfid_tag_id ? (
                                <button
                                  type="button"
                                  style={locationLinkStyle}
                                  onClick={() => openConveyorDetail(row)}
                                >
                                  {path}
                                </button>
                              ) : (
                                path
                              )}
                            </div>
                          );
                        }
                        const lastPart = parts[parts.length - 1];
                        const parentPath = parts.slice(0, -1).join(' > ');
                        return (
                          <div>
                            <div style={{ fontSize: '14px', color: theme === 'dark' ? '#dae3ed' : '#2d3748' }}>
                              {row.rfid_tag_id ? (
                                <button
                                  type="button"
                                  style={locationLinkStyle}
                                  onClick={() => openConveyorDetail(row)}
                                >
                                  {lastPart}
                                </button>
                              ) : (
                                <strong>{lastPart}</strong>
                              )}
                            </div>
                            <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                              {parentPath}
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                  );
                case 'rfid_tag_id':
                  return (
                    <td key={column.id} style={{...cellStyle, fontFamily: 'monospace', fontSize: '1.15em'}}>
                      {row.rfid_tag_id || '-'}
                    </td>
                  );
                case 'result_status':
                  return <td key={column.id} style={cellStyle}>{getStatusBadge(row.result_status)}</td>;
                case 'started_at':
                  return <td key={column.id} style={cellStyle}>{formatDateTime(row.started_at)}</td>;
                case 'ended_at':
                  return <td key={column.id} style={cellStyle}>{formatDateTime(row.ended_at)}</td>;
                case 'duration_seconds':
                  return <td key={column.id} style={cellStyle}>{formatDuration(row.duration_seconds)}</td>;
                default:
                  return undefined;
              }
            }}
          />

          {showConveyorModal && selectedConveyor && (
            <Modal
              title="コンベア詳細"
              onClose={() => setShowConveyorModal(false)}
              width="860px"
            >
              <div style={{ marginBottom: '14px', padding: '12px', borderRadius: '6px', background: theme === 'dark' ? '#2f3844' : '#f7fafc' }}>
                <strong>{selectedConveyor.locationName}</strong>
                <span style={{ marginLeft: '10px', color: theme === 'dark' ? '#a8b3c2' : '#4a5568' }}>({selectedConveyor.locationCode})</span>
                <div style={{ marginTop: '6px', fontSize: '13px', color: theme === 'dark' ? '#a8b3c2' : '#4a5568' }}>
                  RFIDタグ: {selectedConveyor.rfidTagId}
                </div>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <h4 style={{ marginBottom: '8px' }}>コンベア仕様</h4>
                <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr><th style={{ textAlign: 'left', width: '160px', padding: '6px' }}>メーカー</th><td style={{ padding: '6px' }}>{selectedConveyor.conveyorSpec.maker}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>型式</th><td style={{ padding: '6px' }}>{selectedConveyor.conveyorSpec.model}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>モーター型式</th><td style={{ padding: '6px' }}>{selectedConveyor.conveyorSpec.motorModel}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>定格電流値</th><td style={{ padding: '6px' }}>{selectedConveyor.conveyorSpec.ratedCurrent}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>コンベア種別</th><td style={{ padding: '6px' }}>{selectedConveyor.conveyorSpec.conveyorType}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>全長</th><td style={{ padding: '6px' }}>{selectedConveyor.conveyorSpec.totalLength}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>有効幅</th><td style={{ padding: '6px' }}>{selectedConveyor.conveyorSpec.effectiveWidth}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>プーリー径</th><td style={{ padding: '6px' }}>{selectedConveyor.conveyorSpec.pulleyDiameter}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>ベルト速度</th><td style={{ padding: '6px' }}>{selectedConveyor.conveyorSpec.beltSpeed}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>点検周期</th><td style={{ padding: '6px' }}>{selectedConveyor.conveyorSpec.inspectionCycle}</td></tr>
                  </tbody>
                </table>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <h4 style={{ marginBottom: '8px' }}>コンベアベルト情報</h4>
                <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr><th style={{ textAlign: 'left', width: '160px', padding: '6px' }}>メーカー</th><td style={{ padding: '6px' }}>{selectedConveyor.beltSpec.maker}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>型番</th><td style={{ padding: '6px' }}>{selectedConveyor.beltSpec.modelNo}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>材質</th><td style={{ padding: '6px' }}>{selectedConveyor.beltSpec.material}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>長さ</th><td style={{ padding: '6px' }}>{selectedConveyor.beltSpec.length}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>幅</th><td style={{ padding: '6px' }}>{selectedConveyor.beltSpec.width}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>厚み</th><td style={{ padding: '6px' }}>{selectedConveyor.beltSpec.thickness}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>接合方式</th><td style={{ padding: '6px' }}>{selectedConveyor.beltSpec.jointType}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>蛇行防止桟</th><td style={{ padding: '6px' }}>{selectedConveyor.beltSpec.antiWanderCleat}</td></tr>
                    <tr><th style={{ textAlign: 'left', padding: '6px' }}>在庫の有無</th><td style={{ padding: '6px' }}>{selectedConveyor.beltSpec.inStock}</td></tr>
                  </tbody>
                </table>
                <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
                  <button type="button" className="btn btn-secondary">見積依頼</button>
                  <button type="button" className="btn btn-primary">発注</button>
                </div>
              </div>

              <div>
                <h4 style={{ marginBottom: '8px' }}>コンベア履歴</h4>
                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px' }}>取り付け日</th>
                      <th style={{ textAlign: 'left', padding: '6px' }}>取り外し日</th>
                      <th style={{ textAlign: 'left', padding: '6px' }}>損傷種別</th>
                      <th style={{ textAlign: 'left', padding: '6px' }}>損傷写真</th>
                      <th style={{ textAlign: 'left', padding: '6px' }}>原因推定</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedConveyor.history.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '6px' }}>{item.installedAt}</td>
                        <td style={{ padding: '6px' }}>{item.removedAt}</td>
                        <td style={{ padding: '6px' }}>{item.damageType}</td>
                        <td style={{ padding: '6px' }}>{item.damagePhoto}</td>
                        <td style={{ padding: '6px' }}>{item.estimatedCause}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Modal>
          )}
        </div>
      )}
    </div>
  );
}

export default Overview;
